import { StoreModel } from 'apis/types';
import {
  createContext,
  useContext,
  useState,
  useRef,
  ReactNode,
  createRef,
  RefObject,
  MutableRefObject,
} from 'react';

type StoreRefModel = {
  index: number;
  storeInfo1Ref: RefObject<HTMLElement | null>; // 상점 문서 1
  storeInfo2Refs: RefObject<HTMLElement | null>[]; // 상점 문서 2 리스트 (운영+배달 정보)
  fileRefs: RefObject<HTMLElement | null>[]; // 상점 파일 문서
};

type PDFContextType = {
  storeRefs: MutableRefObject<StoreRefModel[]>;
  setStoreRefs: (storeList: StoreModel[]) => void;
  getIsReady: (storeList: StoreModel[]) => boolean;
  setStoreInfo2Refs: (storeIndex: number, refs: RefObject<HTMLElement | null>[]) => void;
  getStoreRefsForPDF: () => RefObject<HTMLElement | null>[];
};

const PDFContext = createContext<PDFContextType | undefined>(undefined);

export const usePDF = (): PDFContextType | null => {
  const ctx = useContext(PDFContext);
  if (!ctx) return null;
  return ctx;
};

type PDFProviderProps = {
  children: ReactNode;
};

export const PDFProvider = ({ children }: PDFProviderProps) => {
  // 상점별 ref 관리
  const storeRefs = useRef<StoreRefModel[]>([]);

  // 준비된 페이지 index
  const [readyPages, setReadyPages] = useState<Set<number>>(new Set());

  const addReadyPage = (index: number) => {
    setReadyPages((prev) => new Set([...prev, index]));
  };

  // 상점별 ref model 추가
  const setStoreRefs = (storeList: StoreModel[]) => {
    for (let i = 0; i < storeList.length; i++) {
      if (!storeRefs.current.find(({ index }) => index === i)) {
        storeRefs.current.push({
          index: i,
          storeInfo1Ref: createRef<HTMLElement | null>(),
          storeInfo2Refs: [],
          fileRefs: [createRef<HTMLElement | null>(), createRef<HTMLElement | null>()],
        });
      }
    }
  };

  // 상점 데이터 문서 ref 추가 (파일 문서 외 모두 해당)
  const setStoreInfo2Refs = (storeIndex: number, refs: RefObject<HTMLElement | null>[]) => {
    if (!storeRefs.current.find(({ index }) => index === storeIndex)) {
      storeRefs.current.push({
        index: storeIndex,
        storeInfo1Ref: createRef(),
        storeInfo2Refs: refs,
        fileRefs: [createRef<HTMLElement>(), createRef<HTMLElement>()],
      });
    }

    storeRefs.current[storeIndex].storeInfo2Refs = refs;
    addReadyPage(storeIndex);
  };

  const getStoreRefsForPDF = () => {
    const firstStore = storeRefs.current[0];
    const flatList: RefObject<HTMLElement | null>[] = [firstStore.storeInfo1Ref];

    storeRefs.current.forEach((store) => {
      flatList.push(...store.storeInfo2Refs);
    });

    flatList.push(...firstStore.fileRefs);
    return flatList;
  };

  const getIsReady = (storeList: StoreModel[]) => {
    for (let i = 0; i < storeList.length; i++) {
      if (!readyPages.has(i)) return false;
    }

    return true;
  };

  return (
    <PDFContext.Provider
      value={{
        storeRefs,
        getIsReady,
        setStoreRefs,
        setStoreInfo2Refs,
        getStoreRefsForPDF,
      }}
    >
      {children}
    </PDFContext.Provider>
  );
};
